import React, { useState } from "react"
import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons"
import { Flex, Form, Input, Upload, message } from "antd"
import type { UploadFile } from "antd"
import { Button } from "../../components/Button"
import { useTranslation } from "react-i18next"
import { CRYPTO_NEWS_URL, FT_NEWS_URL } from "../../constants"
import { ContentWrapper } from "../../components/Wrappers"
import { Header } from "../../components/Text"

type FormItem = {
  url: string
  image: UploadFile[]
}

type FormValues = {
  urlsForFacebook: FormItem[]
  urlsForInstagram: FormItem[]
}

export const SharePosts: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm<FormValues>()

  const onFinish = async (values: FormValues) => {
    setIsLoading(true)
    console.log("Received values of form:", values)

    // Process Facebook posts
    for (const item of values.urlsForFacebook) {
      const { url, image } = item
      const imageFile = image?.[0]?.originFileObj
      console.log(
        `Processing Facebook post: ${url}`,
        imageFile ? "with image" : "without image",
      )
      // TODO: Implement API call to share post
    }

    // Process Instagram posts
    for (const item of values.urlsForInstagram) {
      const { url, image } = item
      const imageFile = image?.[0]?.originFileObj
      console.log(
        `Processing Instagram post: ${url}`,
        imageFile ? "with image" : "without image",
      )
      // TODO: Implement API call to share post
    }

    setIsLoading(false)
  }

  return (
    <ContentWrapper>
      <Form
        form={form}
        name="dynamic_form_item"
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          urlsForFacebook: [{ url: "", image: [] }],
          urlsForInstagram: [{ url: "", image: [] }],
        }}
      >
        {/* FACEBOOK FORM */}
        <Form.List name="urlsForFacebook">
          {(fields, { add, remove }, { errors }) => (
            <>
              <Header children={t("sharingPosts.facebookArticles")} level={3} />
              {fields.map((field, index) => (
                <Flex key={field.key} gap="middle" align="start">
                  <Form.Item
                    label={index === 0 ? t("copying.urlFieldName") : ""}
                    required={false}
                    style={{ flex: 1, marginBottom: 8 }}
                  >
                    <Form.Item
                      name={[field.name, "url"]}
                      validateTrigger={["onChange", "onBlur"]}
                      rules={[
                        {
                          required: true,
                          message: t("copying.wrongUrlErrMsg"),
                        },
                        {
                          validator: (_, value) =>
                            new Promise<void>((resolve, reject) => {
                              setTimeout(() => {
                                if (!value) {
                                  resolve()
                                  return
                                }
                                if (
                                  value.startsWith(CRYPTO_NEWS_URL) ||
                                  value.startsWith(FT_NEWS_URL)
                                ) {
                                  resolve()
                                } else {
                                  reject(new Error(t("copying.wrongUrlErrMsg")))
                                }
                              }, 500)
                            }),
                        },
                      ]}
                      noStyle
                    >
                      <Input
                        readOnly={isLoading}
                        placeholder={t("copying.urlFieldPlaceholder")}
                      />
                    </Form.Item>
                  </Form.Item>
                  <Form.Item
                    label={index === 0 ? t("sharingPosts.imageFieldName") : ""}
                    style={{ marginBottom: 8, width: 250 }}
                  >
                    <Form.Item
                      name={[field.name, "image"]}
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e
                        return e?.fileList
                      }}
                      noStyle
                    >
                      <Upload
                        beforeUpload={(file) => {
                          const isValidType = [
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                            "image/webp",
                          ].includes(file.type)
                          if (!isValidType) {
                            message.error(t("sharingPosts.wrongImageType"))
                          }
                          return isValidType ? false : Upload.LIST_IGNORE
                        }}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.webp"
                        listType="picture"
                        disabled={isLoading}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          label={t("sharingPosts.selectImage")}
                          disabled={isLoading}
                          style={{ width: "250px" }}
                        />
                      </Upload>
                    </Form.Item>
                  </Form.Item>
                  {fields.length > 1 && (
                    <DeleteOutlined
                      style={{
                        cursor: isLoading ? "not-allowed" : "pointer",
                        marginTop: index === 0 ? 32 : 8,
                        fontSize: 16,
                      }}
                      onClick={() => {
                        if (!isLoading) {
                          remove(field.name)
                        }
                      }}
                    />
                  )}
                </Flex>
              ))}
              <Flex justify="center">
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      if (!isLoading) {
                        add({ url: "", image: [] })
                      }
                    }}
                    style={{ width: "300px" }}
                    icon={<PlusOutlined />}
                    label={t("copying.addBtn")}
                    disabled={isLoading}
                  />
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </Flex>
            </>
          )}
        </Form.List>
        {/* INSTAGRAM FORM */}
        <Form.List name="urlsForInstagram">
          {(fields, { add, remove }, { errors }) => (
            <>
              <Header
                children={t("sharingPosts.instagramArticles")}
                level={3}
              />
              {fields.map((field, index) => (
                <Flex key={field.key} gap="middle" align="start">
                  <Form.Item
                    label={index === 0 ? t("copying.urlFieldName") : ""}
                    required={false}
                    style={{ flex: 1, marginBottom: 8 }}
                  >
                    <Form.Item
                      name={[field.name, "url"]}
                      validateTrigger={["onChange", "onBlur"]}
                      rules={[
                        {
                          required: true,
                          message: t("copying.wrongUrlErrMsg"),
                        },
                        {
                          validator: (_, value) =>
                            new Promise<void>((resolve, reject) => {
                              setTimeout(() => {
                                if (!value) {
                                  resolve()
                                  return
                                }
                                if (
                                  value.startsWith(CRYPTO_NEWS_URL) ||
                                  value.startsWith(FT_NEWS_URL)
                                ) {
                                  resolve()
                                } else {
                                  reject(new Error(t("copying.wrongUrlErrMsg")))
                                }
                              }, 500)
                            }),
                        },
                      ]}
                      noStyle
                    >
                      <Input
                        readOnly={isLoading}
                        placeholder={t("copying.urlFieldPlaceholder")}
                      />
                    </Form.Item>
                  </Form.Item>
                  <Form.Item
                    label={index === 0 ? t("sharingPosts.imageFieldName") : ""}
                    style={{ marginBottom: 8, width: 250 }}
                  >
                    <Form.Item
                      name={[field.name, "image"]}
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e
                        return e?.fileList
                      }}
                      rules={[
                        {
                          required: true,
                          message: t("sharingPosts.imageRequired"),
                        },
                      ]}
                      noStyle
                    >
                      <Upload
                        beforeUpload={(file) => {
                          const isValidType = [
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                            "image/webp",
                          ].includes(file.type)
                          if (!isValidType) {
                            message.error(t("sharingPosts.wrongImageType"))
                          }
                          return isValidType ? false : Upload.LIST_IGNORE
                        }}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.webp"
                        listType="picture"
                        disabled={isLoading}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          label={t("sharingPosts.selectImage")}
                          disabled={isLoading}
                          style={{ width: "250px" }}
                        />
                      </Upload>
                    </Form.Item>
                  </Form.Item>
                  {fields.length > 1 && (
                    <DeleteOutlined
                      style={{
                        cursor: isLoading ? "not-allowed" : "pointer",
                        marginTop: index === 0 ? 32 : 8,
                        fontSize: 16,
                      }}
                      onClick={() => {
                        if (!isLoading) {
                          remove(field.name)
                        }
                      }}
                    />
                  )}
                </Flex>
              ))}
              <Flex justify="center">
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      if (!isLoading) {
                        add({ url: "", image: [] })
                      }
                    }}
                    style={{ width: "300px" }}
                    icon={<PlusOutlined />}
                    label={t("copying.addBtn")}
                    disabled={isLoading}
                  />
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </Flex>
            </>
          )}
        </Form.List>
        <Flex justify="center">
          <Form.Item>
            <Button
              type="primary"
              label={t("sharingPosts.shareBtn")}
              htmlType="submit"
              style={{ width: "300px" }}
              loading={isLoading}
            />
          </Form.Item>
        </Flex>
      </Form>
    </ContentWrapper>
  )
}
